import React, { useCallback } from 'react';
import { isFedRamp } from '@atlaskit/atlassian-context';
import { DropdownItem } from '@atlaskit/dropdown-menu';
import VideoIcon from '@atlaskit/icon/core/video';
import Lozenge from '@atlaskit/lozenge';
import { Flex, Box, Pressable, xcss } from '@atlaskit/primitives';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { AdControlProvider } from '@atlassian/ad-control-toolkit';
import { useLoomCrossJoinUrl } from '@atlassian/jira-loom-utils/src/controllers/use-loom-cross-join-url/index.tsx';
import AdControls from '@atlassian/jira-loom-utils/src/ui/ad-controls/index.tsx';
import { LOOM_QUICK_ADD_AD_CONTROL_DISMISS_KEY } from '@atlassian/jira-issue-view-common/src/component/loom-videos/constants.tsx';
import { LoomTouchpointVariant } from '@atlassian/jira-loom-utils/src/controllers/use-loom-touchpoint-variant/types.tsx';
import { useLoomTouchpointVariantWithAdControls } from '@atlassian/jira-issue-view-common/src/component/loom-videos/use-loom-touchpoint-variant-with-ad-controls/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useIsSiteAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-site-admin/index.tsx';
import messages from '../messages.tsx';

const CrossJoinRecordItemWithNestedInteractive = ({
	dismissAd,
}: {
	dismissAd: () => Promise<void>;
}) => {
	const { formatMessage } = useIntl();
	const crossJoinUrl = useLoomCrossJoinUrl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isSiteAdmin = useIsSiteAdmin();

	const handleClick = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'dropdownItem',
			}),
			'add-icon-button-dropdown-record-a-video-cross-join-button',
			{ isSiteAdmin },
		);
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		window.open(crossJoinUrl, '_blank', 'noreferrer');
	}, [createAnalyticsEvent, isSiteAdmin, crossJoinUrl]);

	return (
		<AdControlProvider>
			<DropdownItem
				key="loom-section-actions-menu-cross-join-recording-button"
				elemBefore={
					<VideoIcon
						label={formatMessage(messages.videoIcon)}
						color="currentColor"
						spacing="spacious"
					/>
				}
				onClick={handleClick}
			>
				<Flex justifyContent="space-between" alignItems="center">
					<Flex alignItems="center" gap="space.050" xcss={anchorStyles}>
						{formatMessage(messages.loomVideo)}
						<Lozenge appearance="new">{formatMessage(messages.tryLozenge)}</Lozenge>
					</Flex>
					{fg('enghealth-40167-remove-nested-interactive') ? (
						<Box
							onClick={(e: React.MouseEvent) => {
								e.stopPropagation();
							}}
							onKeyDown={(e: React.KeyboardEvent) => {
								if (e.key === 'Enter' || e.key === ' ') {
									e.preventDefault();
									e.stopPropagation();
								}
							}}
							xcss={pressableStyles}
							tabIndex={0}
						>
							<AdControls onDismiss={dismissAd} menuLocation="quick-add" />
						</Box>
					) : (
						<Pressable
							onClick={(e: React.MouseEvent) => {
								e.stopPropagation();
							}}
							xcss={pressableStyles}
						>
							<AdControls onDismiss={dismissAd} menuLocation="quick-add" />
						</Pressable>
					)}
				</Flex>
			</DropdownItem>
		</AdControlProvider>
	);
};

export const CrossJoinRecordItem = () => {
	const isFedRampInstance = isFedRamp();
	const { dismissAd, loomTouchpointVariant } = useLoomTouchpointVariantWithAdControls(
		LOOM_QUICK_ADD_AD_CONTROL_DISMISS_KEY,
	);

	return (
		!isFedRampInstance &&
		loomTouchpointVariant === LoomTouchpointVariant.CROSSJOIN &&
		(fg('enghealth-40167-remove-nested-interactive') ? (
			<Box role="menu">
				<CrossJoinRecordItemWithNestedInteractive dismissAd={dismissAd} />
			</Box>
		) : (
			<CrossJoinRecordItemWithNestedInteractive dismissAd={dismissAd} />
		))
	);
};

const pressableStyles = xcss({
	backgroundColor: 'color.background.neutral.subtle',
	padding: 'space.0',
});

const anchorStyles = xcss({
	flexGrow: 1,
});
